import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchServiceCards, fetchMenu } from "../api";

// Deterministic pseudo-random scatter (avoids Math.random() re-shuffling stars on every render).
function seededRandom(seed) {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}

const STARS = [...Array(40)].map((_, i) => ({
    top: `${Math.round(seededRandom(i * 12.9898) * 100)}%`,
    left: `${Math.round(seededRandom(i * 78.233) * 100)}%`,
    size: seededRandom(i * 37.719) > 0.5 ? 3 : 2,
    duration: `${(4 + seededRandom(i * 4.567) * 5).toFixed(1)}s`,
    delay: `${(seededRandom(i * 91.345) * 4).toFixed(1)}s`,
}));

// The Services grid/stars background, kept as-is.
function BackgroundDecor() {
    return (
        <div className="absolute inset-0 z-0">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
                {[...Array(50)].map((_, i) => (
                    <line key={`h-${i}`} x1="0" y1={`${i * 10}%`} x2="100%" y2={`${i * 10}%`} stroke="#233A51" strokeWidth="0.1" />
                ))}
                {[...Array(10)].map((_, i) => (
                    <line key={`v-${i}`} x1={`${i * 10}%`} y1="0" x2={`${i * 10}%`} y2="100%" stroke="#233A51" strokeWidth="0.1" />
                ))}
            </svg>
            {STARS.map((star, i) => (
                <span
                    key={`star-${i}`}
                    className="twinkle-star absolute rounded-full bg-accent-light"
                    style={{ top: star.top, left: star.left, width: star.size, height: star.size, animationDuration: star.duration, animationDelay: star.delay }}
                />
            ))}
        </div>
    );
}

// AI Tools gets a distinct background - a darker base with drifting purple/cyan glow orbs (the
// same floating-light look used on the AI Tools detail pages) instead of the grid+stars pattern,
// so the section reads as visually different from Services rather than a repeat of it.
function AIBackgroundDecor() {
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="floating-light absolute -top-40 -left-20 w-[500px] h-[500px] rounded-full bg-purple-500/20 blur-[120px]" style={{ animationDuration: "20s" }} />
            <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "24s", animationDelay: "2s" }} />
            <div className="floating-light absolute top-1/2 left-1/2 w-[350px] h-[350px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "18s", animationDelay: "4s" }} />
        </div>
    );
}

function ServiceCardsGrid({ cards, linkFor }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {cards.map((card) => (
                <motion.div
                    key={card.id}
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="group relative rounded-xl bg-navy-dark border border-accent-light/25 shadow-[0_0_15px_rgba(34,211,238,0.2)] overflow-hidden transition-all duration-300 hover:border-accent-light hover:shadow-[0_0_35px_rgba(34,211,238,0.6)]"
                >
                    <Link to={linkFor(card)} className="block">
                        <div className="relative bg-navy overflow-hidden">
                            {card.image ? (
                                <img
                                    src={card.image}
                                    alt={card.title}
                                    className="w-full h-auto block transition-transform duration-500 group-hover:scale-105"
                                />
                            ) : (
                                <div className="w-full h-48 bg-navy" />
                            )}
                        </div>
                        <div className="p-6">
                            <h3 className="text-xl font-semibold group-hover:text-accent-light transition-colors">
                                {card.title}
                            </h3>
                            {card.subtitle && (
                                <p className="text-gray-400 text-sm mt-2">{card.subtitle}</p>
                            )}
                        </div>
                    </Link>
                </motion.div>
            ))}
        </div>
    );
}

// The Services and AI Tools cards both live in the same ServiceCard table (matched to a menu
// category by title), so they're split here by which top-level dropdown ("Services" vs "AI
// Tools") each card's title belongs to - two separate containers, each linking to its own kind
// of page (a Service's own /services/:id detail page vs. an AI Tools category's workspace page).
export default function HomeServices() {
    const [serviceCards, setServiceCards] = useState([]);
    const [aiToolCards, setAiToolCards] = useState([]);
    const [aiToolSlugs, setAiToolSlugs] = useState({}); // title -> slug, for AI Tools category links

    useEffect(() => {
        Promise.all([fetchServiceCards(), fetchMenu()])
            .then(([cards, menuItems]) => {
                const servicesMenu = menuItems.find((m) => m.parent === null && m.title === "Services");
                const aiToolsMenu = menuItems.find((m) => m.parent === null && m.title === "AI Tools");
                const serviceTitles = servicesMenu
                    ? new Set(menuItems.filter((m) => m.parent === servicesMenu.id).map((m) => m.title))
                    : new Set();
                const aiTools = aiToolsMenu
                    ? menuItems.filter((m) => m.parent === aiToolsMenu.id)
                    : [];
                const aiToolTitles = new Set(aiTools.map((m) => m.title));
                setAiToolSlugs(Object.fromEntries(aiTools.map((m) => [m.title, m.slug])));

                setServiceCards(cards.filter((c) => serviceTitles.has(c.title)));
                setAiToolCards(cards.filter((c) => aiToolTitles.has(c.title)));
            })
            .catch(() => {});
    }, []);

    if (serviceCards.length === 0 && aiToolCards.length === 0) return null;

    return (
        <>
            {serviceCards.length > 0 && (
                <section id="services" className="relative py-20 scroll-mt-20 text-white bg-navy border-b border-navy-border">
                    <BackgroundDecor />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                        <h2 className="gradient-text-animated text-4xl md:text-5xl font-bold text-center mb-12">
                            Our Services
                        </h2>
                        <ServiceCardsGrid cards={serviceCards} linkFor={(card) => `/services/${card.id}`} />
                    </div>
                </section>
            )}

            {aiToolCards.length > 0 && (
                <section id="ai-tools" className="relative py-20 scroll-mt-20 text-white bg-navy-darker border-b border-navy-border overflow-hidden">
                    <AIBackgroundDecor />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                        <h2 className="gradient-text-animated text-4xl md:text-5xl font-bold text-center mb-12">
                            AI Tools
                        </h2>
                        <ServiceCardsGrid cards={aiToolCards} linkFor={(card) => `/${aiToolSlugs[card.title] || ""}`} />
                    </div>
                </section>
            )}
        </>
    );
}
